import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs, orderBy, serverTimestamp, addDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { Icons } from '../components/Icons';
import { sendSlackNotification } from '../utils/slackNotification';
import { getUserDisplayName } from '../utils/userMapping';
import { sendLocationChangeNotification, sendQuantityChangeNotification } from '../utils/sendNotification';

function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [qrCode, setQrCode] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const barcodeRef = useRef(null);

  // 이미지 업로드 관련
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);

  // 이미지 모달 관련
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // SKU 부분 이동 모달 관련
  const [showSkuMoveModal, setShowSkuMoveModal] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [targetLocation, setTargetLocation] = useState('');
  const [targetZone, setTargetZone] = useState({ id: '', name: '' });
  const [allLocations, setAllLocations] = useState([]);
  const [allZones, setAllZones] = useState([]);

  // SKU 부분 삭제 모달 관련
  const [showSkuDeleteModal, setShowSkuDeleteModal] = useState(false);
  const [skusToDelete, setSkusToDelete] = useState([]);

  // 판매유무/상태 옵션 관리 모달 관련
  const [showStatusOptionsModal, setShowStatusOptionsModal] = useState(false);
  const [statusOptionsType, setStatusOptionsType] = useState(''); // 'isSold' or 'status'
  const [isSoldOptions, setIsSoldOptions] = useState(['판매중', '판매완료']);
  const [statusOptions, setStatusOptions] = useState(['판매가능', '수리중', '청소중', '예약', '판매완료']);
  const [newOptionInput, setNewOptionInput] = useState('');
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [editingOptionValue, setEditingOptionValue] = useState('');

  // SKU별 컨디션 관리
  const [selectedSkuForCondition, setSelectedSkuForCondition] = useState(null);
  const [conditionOptions, setConditionOptions] = useState(['최상', '상', '중', '하']);
  const [showConditionOptionsModal, setShowConditionOptionsModal] = useState(false);
  const [newConditionOptionInput, setNewConditionOptionInput] = useState('');
  const [editingConditionIndex, setEditingConditionIndex] = useState(null);
  const [editingConditionValue, setEditingConditionValue] = useState('');
  // SKU별 컨디션 설명
  const [editingConditionNote, setEditingConditionNote] = useState(null);
  const [conditionNoteInput, setConditionNoteInput] = useState('');
  // SKU별 판매상태
  const [selectedSkuForStatus, setSelectedSkuForStatus] = useState(null);
  const conditionColors = {
    '최상': 'bg-green-100 text-green-700 border-green-200',
    '상': 'bg-blue-100 text-blue-700 border-blue-200',
    '중': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    '하': 'bg-red-100 text-red-700 border-red-200'
  };

  const locations = [
    '자곡동 매장',
    '누룽지 창고',
    '자곡동 창고',
    '직동 창고',
    '마켓',
    '예술가구',
    '남성쇼파',
    '복원완료'
  ];

  useEffect(() => {
    loadItem();
    loadHistory();
    loadLocations();
    loadStatusOptions();
    loadConditionOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 판매유무/상태 옵션 불러오기
  const loadStatusOptions = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'statusOptions');
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.isSoldOptions?.length > 0) setIsSoldOptions(data.isSoldOptions);
        if (data.statusOptions?.length > 0) setStatusOptions(data.statusOptions);
      }
    } catch (error) {
      console.error('상태 옵션 불러오기 오류:', error);
    }
  };

  // 판매유무/상태 옵션 저장하기
  const saveStatusOptions = async (type, options) => {
    try {
      const settingsRef = doc(db, 'settings', 'statusOptions');
      const updateData = type === 'isSold' ? { isSoldOptions: options } : { statusOptions: options };
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, updateData);
      } else {
        // setDoc으로 지정된 ID로 문서 생성
        await setDoc(settingsRef, updateData);
      }
    } catch (error) {
      console.error('상태 옵션 저장 오류:', error);
      alert('옵션 저장 중 오류가 발생했습니다.');
    }
  };

  // 컨디션 옵션 불러오기
  const loadConditionOptions = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'conditionOptions');
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.options?.length > 0) setConditionOptions(data.options);
      }
    } catch (error) {
      console.error('컨디션 옵션 불러오기 오류:', error);
    }
  };

  // 컨디션 옵션 저장하기
  const saveConditionOptions = async (options) => {
    try {
      const settingsRef = doc(db, 'settings', 'conditionOptions');
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, { options });
      } else {
        await setDoc(settingsRef, { options });
      }
    } catch (error) {
      console.error('컨디션 옵션 저장 오류:', error);
      alert('옵션 저장 중 오류가 발생했습니다.');
    }
  };

  // 컨디션 옵션 추가
  const handleAddConditionOption = () => {
    if (!newConditionOptionInput.trim()) return;
    if (conditionOptions.includes(newConditionOptionInput.trim())) {
      alert('이미 존재하는 옵션입니다.');
      return;
    }
    const newOptions = [...conditionOptions, newConditionOptionInput.trim()];
    setConditionOptions(newOptions);
    saveConditionOptions(newOptions);
    setNewConditionOptionInput('');
  };

  // 컨디션 옵션 수정
  const handleUpdateConditionOption = (index) => {
    if (!editingConditionValue.trim()) return;
    if (conditionOptions.includes(editingConditionValue.trim()) && conditionOptions[index] !== editingConditionValue.trim()) {
      alert('이미 존재하는 옵션입니다.');
      return;
    }
    const newOptions = [...conditionOptions];
    newOptions[index] = editingConditionValue.trim();
    setConditionOptions(newOptions);
    saveConditionOptions(newOptions);
    setEditingConditionIndex(null);
    setEditingConditionValue('');
  };

  // 컨디션 옵션 삭제
  const handleDeleteConditionOption = (index) => {
    if (conditionOptions.length <= 1) {
      alert('최소 1개의 옵션이 필요합니다.');
      return;
    }
    const newOptions = conditionOptions.filter((_, i) => i !== index);
    setConditionOptions(newOptions);
    saveConditionOptions(newOptions);
  };

  // 옵션 추가
  const handleAddOption = () => {
    if (!newOptionInput.trim()) return;
    const currentOptions = statusOptionsType === 'isSold' ? isSoldOptions : statusOptions;
    if (currentOptions.includes(newOptionInput.trim())) {
      alert('이미 존재하는 옵션입니다.');
      return;
    }
    const newOptions = [...currentOptions, newOptionInput.trim()];
    if (statusOptionsType === 'isSold') {
      setIsSoldOptions(newOptions);
    } else {
      setStatusOptions(newOptions);
    }
    saveStatusOptions(statusOptionsType, newOptions);
    setNewOptionInput('');
  };

  // 옵션 수정
  const handleUpdateOption = (index) => {
    if (!editingOptionValue.trim()) return;
    const currentOptions = statusOptionsType === 'isSold' ? [...isSoldOptions] : [...statusOptions];
    if (currentOptions.includes(editingOptionValue.trim()) && currentOptions[index] !== editingOptionValue.trim()) {
      alert('이미 존재하는 옵션입니다.');
      return;
    }
    currentOptions[index] = editingOptionValue.trim();
    if (statusOptionsType === 'isSold') {
      setIsSoldOptions(currentOptions);
    } else {
      setStatusOptions(currentOptions);
    }
    saveStatusOptions(statusOptionsType, currentOptions);
    setEditingOptionIndex(null);
    setEditingOptionValue('');
  };

  // 옵션 삭제
  const handleDeleteOption = (index) => {
    const currentOptions = statusOptionsType === 'isSold' ? [...isSoldOptions] : [...statusOptions];
    if (currentOptions.length <= 1) {
      alert('최소 1개의 옵션이 필요합니다.');
      return;
    }
    if (!window.confirm(`'${currentOptions[index]}' 옵션을 삭제하시겠습니까?`)) return;
    currentOptions.splice(index, 1);
    if (statusOptionsType === 'isSold') {
      setIsSoldOptions(currentOptions);
    } else {
      setStatusOptions(currentOptions);
    }
    saveStatusOptions(statusOptionsType, currentOptions);
  };

  // Firestore에서 위치 목록 로드 (order 순으로 정렬)
  const loadLocations = async () => {
    try {
      const locationsRef = collection(db, 'locations');
      const locationsSnapshot = await getDocs(locationsRef);
      const locationsList = locationsSnapshot.docs
        .map(doc => ({ id: doc.id, name: doc.data().name, order: doc.data().order ?? 999 }))
        .sort((a, b) => a.order - b.order);
      setAllLocations(locationsList.map(loc => loc.name));

      // Zone 목록도 로드
      const zonesRef = collection(db, 'zones');
      const zonesSnapshot = await getDocs(zonesRef);
      const zonesList = zonesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          name: doc.data().name,
          locationId: doc.data().locationId,
          locationName: doc.data().locationName,
          order: doc.data().order ?? 999,
          active: doc.data().active !== false
        }))
        .filter(zone => zone.active)
        .sort((a, b) => {
          if (a.locationName !== b.locationName) return a.locationName?.localeCompare(b.locationName) || 0;
          return a.order - b.order;
        });
      setAllZones(zonesList);
    } catch (error) {
      console.error('위치/Zone 로드 오류:', error);
    }
  };

  useEffect(() => {
    if (item?.barcodeId) {
      generateQRCode(item.barcodeId);
      generateBarcode(item.barcodeId);
    }
    // 다량 제품에 skuLocations가 없으면 초기화
    if (item && !item.skuLocations && (Number(item.quantity) > 1 || item.productType === 'bulk')) {
      initializeSkuLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const loadItem = async () => {
    try {
      const docRef = doc(db, 'inventory', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setItem(data);
        setEditData(data);
      } else {
        alert('재고를 찾을 수 없습니다.');
        navigate('/inventory');
      }
    } catch (error) {
      console.error('재고 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const historyRef = collection(db, 'inventory_history');
      const q = query(
        historyRef,
        where('productId', '==', id),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);

      const historyData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setHistory(historyData);
    } catch (error) {
      console.error('이력 로드 오류:', error);
    }
  };

  // SKU 위치별 그룹화 함수
  const groupSkusByLocation = (skuLocations) => {
    if (!skuLocations || skuLocations.length === 0) return {};
    return skuLocations.reduce((acc, item) => {
      const loc = item.location || '미지정';
      if (!acc[loc]) acc[loc] = [];
      acc[loc].push(item);
      return acc;
    }, {});
  };

  // SKU 위치 요약 텍스트 생성 (다량 제품용)
  const getSkuLocationSummary = (skuLocations) => {
    if (!skuLocations || skuLocations.length === 0) return null;
    const grouped = groupSkusByLocation(skuLocations);
    const entries = Object.entries(grouped);
    if (entries.length === 0) return null;
    // "직동 창고 (6개)" 또는 "직동 창고 (4개), 자곡동 매장 (2개)" 형식
    return entries.map(([loc, items]) => `${loc} (${items.length}개)`).join(', ');
  };

  // 다량 제품인지 확인 (SKU 위치 데이터가 있는 제품)
  const hasBulkSkuLocations = item?.skuLocations && item.skuLocations.length > 0;

  // 위치/Zone 불일치 감지
  const getLocationZoneMismatch = () => {
    if (!item?.zoneName) return null;

    // Zone에서 위치 부분 추출 (예: "자곡동 매장 - MAIN" → "자곡동 매장")
    const zoneLocationPart = item.zoneName.split(' - ')[0];

    if (hasBulkSkuLocations) {
      // 다량 제품: SKU 위치들과 Zone 위치 비교
      const skuLocations = item.skuLocations.map(sku => sku.location).filter(Boolean);
      const uniqueLocations = [...new Set(skuLocations)];

      // SKU 위치 중 하나라도 Zone 위치와 다르면 불일치
      const hasMatchingLocation = uniqueLocations.some(loc => loc === zoneLocationPart);
      if (!hasMatchingLocation && uniqueLocations.length > 0) {
        return {
          type: 'sku_zone_mismatch',
          message: `SKU 위치(${uniqueLocations.join(', ')})와 Zone(${zoneLocationPart})이 다릅니다`,
          skuLocations: uniqueLocations,
          zoneLocation: zoneLocationPart
        };
      }
    } else {
      // 단품: 제품 위치와 Zone 위치 비교
      if (item.location && item.location !== zoneLocationPart) {
        return {
          type: 'location_zone_mismatch',
          message: `위치(${item.location})와 Zone(${zoneLocationPart})이 다릅니다`,
          productLocation: item.location,
          zoneLocation: zoneLocationPart
        };
      }
    }
    return null;
  };

  const locationMismatch = getLocationZoneMismatch();

  // skuLocations 초기화 (기존 데이터 호환)
  const initializeSkuLocations = async () => {
    if (!item || item.skuLocations) return;

    const quantity = Number(item.quantity) || 1;
    if (quantity <= 1 && item.productType !== 'bulk') return;

    const skuLocations = Array.from({ length: quantity }, (_, i) => ({
      sku: String(i + 1).padStart(2, '0'),
      location: item.location || '',
      condition: item.condition || '중'  // 제품 컨디션 상속
    }));

    try {
      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, { skuLocations });
      setItem(prev => ({ ...prev, skuLocations }));
    } catch (error) {
      console.error('SKU 위치 초기화 오류:', error);
    }
  };

  // 선택된 SKU들 이동 처리
  const handleSkuMove = async () => {
    if (selectedSkus.length === 0 || !targetLocation) {
      alert('이동할 SKU와 목적지를 선택해주세요.');
      return;
    }

    try {
      const updatedSkuLocations = item.skuLocations.map(sku => {
        if (selectedSkus.includes(sku.sku)) {
          return {
            ...sku,
            location: targetLocation,
            zoneId: targetZone.id || '',
            zoneName: targetZone.name || ''
          };
        }
        return sku;
      });

      // 가장 많은 SKU가 있는 위치를 대표 위치로 설정
      const locationCounts = {};
      updatedSkuLocations.forEach(sku => {
        locationCounts[sku.location] = (locationCounts[sku.location] || 0) + 1;
      });
      const mainLocation = Object.entries(locationCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || item.location;

      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, {
        skuLocations: updatedSkuLocations,
        location: mainLocation,
        updatedAt: serverTimestamp()
      });

      // 히스토리 기록
      const previousLocation = item.skuLocations.find(s => s.sku === selectedSkus[0])?.location;
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: 'SKU위치변경',
        previousValue: previousLocation,
        newValue: targetLocation,
        movedSkus: selectedSkus,
        skuCount: selectedSkus.length,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      // Slack 알림
      await sendSlackNotification({
        type: 'sku_move',
        productName: item.productName,
        barcodeId: item.barcodeId,
        movedSkus: selectedSkus.map(s => `${item.barcodeId}-${s}`),
        fromLocation: previousLocation,
        toLocation: targetLocation,
        user: getUserDisplayName(user.email)
      });

      setItem(prev => ({ ...prev, skuLocations: updatedSkuLocations, location: mainLocation }));
      setShowSkuMoveModal(false);
      setSelectedSkus([]);
      setTargetLocation('');
      setTargetZone({ id: '', name: '' });
      loadHistory();
      alert(`${selectedSkus.length}개의 SKU가 ${targetLocation}(으)로 이동되었습니다.`);
    } catch (error) {
      console.error('SKU 이동 오류:', error);
      alert('SKU 이동 중 오류가 발생했습니다.');
    }
  };

  // SKU 선택 토글
  const toggleSkuSelection = (sku) => {
    setSelectedSkus(prev =>
      prev.includes(sku) ? prev.filter(s => s !== sku) : [...prev, sku]
    );
  };

  // 특정 위치의 모든 SKU 선택/해제
  const toggleLocationSkus = (location, skus) => {
    const skuIds = skus.map(s => s.sku);
    const allSelected = skuIds.every(sku => selectedSkus.includes(sku));

    if (allSelected) {
      setSelectedSkus(prev => prev.filter(s => !skuIds.includes(s)));
    } else {
      setSelectedSkus(prev => [...new Set([...prev, ...skuIds])]);
    }
  };

  // SKU 삭제 선택 토글
  const toggleSkuDeleteSelection = (sku) => {
    setSkusToDelete(prev =>
      prev.includes(sku) ? prev.filter(s => s !== sku) : [...prev, sku]
    );
  };

  // 특정 위치의 모든 SKU 삭제 선택/해제
  const toggleLocationSkusDelete = (location, skus) => {
    const skuIds = skus.map(s => s.sku);
    const allSelected = skuIds.every(sku => skusToDelete.includes(sku));

    if (allSelected) {
      setSkusToDelete(prev => prev.filter(s => !skuIds.includes(s)));
    } else {
      setSkusToDelete(prev => [...new Set([...prev, ...skuIds])]);
    }
  };

  // SKU별 컨디션 변경 처리
  const handleSkuConditionChange = async (skuNumber, newCondition) => {
    try {
      const currentSku = item.skuLocations.find(s => s.sku === skuNumber);
      const previousCondition = currentSku?.condition || item.condition || '중';

      const updatedSkuLocations = item.skuLocations.map(sku => {
        if (sku.sku === skuNumber) {
          return { ...sku, condition: newCondition };
        }
        return sku;
      });

      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, { skuLocations: updatedSkuLocations });

      // 히스토리 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: 'SKU컨디션변경',
        previousValue: previousCondition,
        newValue: newCondition,
        skuCode: `${item.barcodeId}-${skuNumber}`,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      setItem(prev => ({ ...prev, skuLocations: updatedSkuLocations }));
      setSelectedSkuForCondition(null);
    } catch (error) {
      console.error('SKU 컨디션 변경 오류:', error);
      alert('컨디션 변경 중 오류가 발생했습니다.');
    }
  };

  // SKU 컨디션 설명 편집 시작
  const startEditConditionNote = (sku) => {
    setEditingConditionNote(sku.sku);
    setConditionNoteInput(sku.conditionNote || '');
  };

  // SKU 컨디션 설명 저장
  const handleSaveConditionNote = async (skuNumber) => {
    try {
      const currentSku = item.skuLocations.find(s => s.sku === skuNumber);
      const previousNote = currentSku?.conditionNote || '';
      const newNote = conditionNoteInput.trim();

      // 변경사항 없으면 저장 안함
      if (previousNote === newNote) {
        setEditingConditionNote(null);
        setConditionNoteInput('');
        return;
      }

      const updatedSkuLocations = item.skuLocations.map(sku => {
        if (sku.sku === skuNumber) {
          return { ...sku, conditionNote: newNote };
        }
        return sku;
      });

      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, { skuLocations: updatedSkuLocations });

      // 히스토리 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: 'SKU비고수정',
        previousValue: previousNote || '(없음)',
        newValue: newNote || '(삭제)',
        skuCode: `${item.barcodeId}-${skuNumber}`,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      setItem(prev => ({ ...prev, skuLocations: updatedSkuLocations }));
      setEditingConditionNote(null);
      setConditionNoteInput('');
    } catch (error) {
      console.error('SKU 컨디션 설명 저장 오류:', error);
    }
  };

  // SKU 판매상태 변경
  const handleSkuStatusChange = async (skuNumber, newStatus) => {
    try {
      const currentSku = item.skuLocations.find(s => s.sku === skuNumber);
      const previousStatus = currentSku?.status || item.status || '판매가능';

      const updatedSkuLocations = item.skuLocations.map(sku => {
        if (sku.sku === skuNumber) {
          return { ...sku, status: newStatus };
        }
        return sku;
      });

      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, { skuLocations: updatedSkuLocations });

      // 히스토리 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: 'SKU판매상태변경',
        previousValue: previousStatus,
        newValue: newStatus,
        skuCode: `${item.barcodeId}-${skuNumber}`,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      setItem(prev => ({ ...prev, skuLocations: updatedSkuLocations }));
      setSelectedSkuForStatus(null);
    } catch (error) {
      console.error('SKU 판매상태 변경 오류:', error);
      alert('판매상태 변경 중 오류가 발생했습니다.');
    }
  };

  // SKU 부분 삭제 처리
  const handleSkuDelete = async () => {
    if (skusToDelete.length === 0) {
      alert('삭제할 SKU를 선택해주세요.');
      return;
    }

    if (skusToDelete.length === item.skuLocations.length) {
      alert('모든 SKU를 삭제하려면 제품 삭제를 이용해주세요.');
      return;
    }

    const confirmMessage = `선택한 ${skusToDelete.length}개의 SKU를 삭제하시겠습니까?\n\n삭제될 SKU: ${skusToDelete.map(s => `${item.barcodeId}-${s}`).join(', ')}\n\n이 작업은 되돌릴 수 없습니다.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      // 선택된 SKU 제외한 나머지
      const updatedSkuLocations = item.skuLocations.filter(sku => !skusToDelete.includes(sku.sku));
      const newQuantity = updatedSkuLocations.length;

      // 가장 많은 SKU가 있는 위치를 대표 위치로 설정
      const locationCounts = {};
      updatedSkuLocations.forEach(sku => {
        locationCounts[sku.location] = (locationCounts[sku.location] || 0) + 1;
      });
      const mainLocation = Object.entries(locationCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || item.location;

      const docRef = doc(db, 'inventory', id);
      await updateDoc(docRef, {
        skuLocations: updatedSkuLocations,
        quantity: newQuantity,
        location: mainLocation,
        updatedAt: serverTimestamp()
      });

      // 히스토리 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: 'SKU삭제',
        previousValue: `수량: ${item.quantity}`,
        newValue: `수량: ${newQuantity}`,
        deletedSkus: skusToDelete.map(s => `${item.barcodeId}-${s}`),
        skuCount: skusToDelete.length,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      // Slack 알림
      await sendSlackNotification({
        type: 'sku_delete',
        productName: item.productName,
        barcodeId: item.barcodeId,
        deletedSkus: skusToDelete.map(s => `${item.barcodeId}-${s}`),
        previousQuantity: item.quantity,
        newQuantity: newQuantity,
        user: getUserDisplayName(user.email)
      });

      setItem(prev => ({ ...prev, skuLocations: updatedSkuLocations, quantity: newQuantity, location: mainLocation }));
      setEditData(prev => ({ ...prev, quantity: newQuantity }));
      setShowSkuDeleteModal(false);
      setSkusToDelete([]);
      loadHistory();
      alert(`${skusToDelete.length}개의 SKU가 삭제되었습니다. (수량: ${item.quantity} → ${newQuantity})`);
    } catch (error) {
      console.error('SKU 삭제 오류:', error);
      alert('SKU 삭제 중 오류가 발생했습니다.');
    }
  };

  const generateQRCode = async (barcodeId) => {
    try {
      const url = await QRCode.toDataURL(barcodeId, {
        width: 200,
        margin: 1,
      });
      setQrCode(url);
    } catch (error) {
      console.error('QR 코드 생성 오류:', error);
    }
  };

  const generateBarcode = (barcodeId) => {
    if (barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, barcodeId, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true
        });
      } catch (error) {
        console.error('바코드 생성 오류:', error);
      }
    }
  };

  const handleEdit = () => {
    setEditData(item);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditData(item);
    setNewImageFiles([]);
    setNewImagePreviews([]);
    setDeletedImages([]);
  };

  const handleNewImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImageFiles(prev => [...prev, ...files]);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeNewImage = (index) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const markImageForDeletion = (imageUrl) => {
    setDeletedImages(prev => [...prev, imageUrl]);
    setEditData(prev => ({
      ...prev,
      photos: prev.photos.filter(url => url !== imageUrl)
    }));
  };

  // 이미지 모달 핸들러
  const openImageModal = (index) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const nextImage = () => {
    if (item?.photos?.length > 0) {
      setSelectedImageIndex((prev) => (prev + 1) % item.photos.length);
    }
  };

  const previousImage = () => {
    if (item?.photos?.length > 0) {
      setSelectedImageIndex((prev) => (prev - 1 + item.photos.length) % item.photos.length);
    }
  };

  const handleSaveEdit = async () => {
    try {
      console.log('=== 수정 저장 시작 ===');
      const docRef = doc(db, 'inventory', id);

      // 삭제된 이미지 처리
      for (const imageUrl of deletedImages) {
        try {
          // Firebase Storage URL에서 파일 경로 추출
          const urlPath = imageUrl.split('/o/')[1]?.split('?')[0];
          if (urlPath) {
            const decodedPath = decodeURIComponent(urlPath);
            const imageRef = ref(storage, decodedPath);
            await deleteObject(imageRef);
            console.log('이미지 삭제 성공:', decodedPath);
          }
        } catch (error) {
          console.error('이미지 삭제 오류:', error);
        }
      }

      // 새 이미지 업로드
      let newPhotoUrls = [];
      if (newImageFiles.length > 0) {
        console.log('새 이미지 업로드 시작:', newImageFiles.length);
        const uploadPromises = newImageFiles.map(async (file, index) => {
          const storageRef = ref(storage, `inventory/${id}/${Date.now()}_${index}_${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          console.log('업로드 완료:', url);
          return url;
        });
        newPhotoUrls = await Promise.all(uploadPromises);
      }

      // 기존 사진 + 새 사진 합치기
      const updatedPhotos = [...(editData.photos || []), ...newPhotoUrls];
      console.log('최종 사진 목록:', updatedPhotos);

      // 변경사항 감지 (undefined 값은 null로 변환하여 Firestore 오류 방지)
      const changes = {};
      Object.keys(editData).forEach(key => {
        if (editData[key] !== item[key] && key !== 'id') {
          changes[key] = {
            old: item[key] === undefined ? null : item[key],
            new: editData[key] === undefined ? null : editData[key]
          };
        }
      });

      if (newImageFiles.length > 0 || deletedImages.length > 0) {
        changes['photos'] = {
          old: item.photos,
          new: updatedPhotos
        };
      }

      if (Object.keys(changes).length === 0 && newImageFiles.length === 0 && deletedImages.length === 0) {
        alert('변경사항이 없습니다.');
        setEditing(false);
        return;
      }

      // 수량 변경 시 SKU 동기화 처리
      let updatedSkuLocations = item.skuLocations || [];
      const currentSkuCount = updatedSkuLocations.length;
      const newQuantity = editData.quantity !== undefined ? Number(editData.quantity) : item.quantity;
      const oldQuantity = item.quantity || 0;

      if (newQuantity !== oldQuantity && currentSkuCount > 0) {
        if (newQuantity < oldQuantity) {
          // 수량 감소: SKU 삭제 확인
          const skuDiff = currentSkuCount - newQuantity;
          if (skuDiff > 0) {
            const confirmSkuDelete = window.confirm(
              `수량이 ${oldQuantity}개에서 ${newQuantity}개로 감소합니다.\n` +
              `현재 ${currentSkuCount}개의 SKU 중 ${skuDiff}개가 삭제됩니다.\n\n` +
              `삭제될 SKU: ${updatedSkuLocations.slice(-skuDiff).map(s => s.sku).join(', ')}\n\n` +
              `계속하시겠습니까?`
            );
            if (!confirmSkuDelete) {
              return;
            }
            // 뒤에서부터 SKU 제거
            updatedSkuLocations = updatedSkuLocations.slice(0, newQuantity);
          }
        } else if (newQuantity > oldQuantity && newQuantity > currentSkuCount) {
          // 수량 증가: 새 SKU 추가
          const addCount = newQuantity - currentSkuCount;
          const currentMaxSku = currentSkuCount > 0
            ? Math.max(...updatedSkuLocations.map(s => parseInt(s.sku) || 0))
            : 0;
          const defaultLocation = item.location || '';

          for (let i = 1; i <= addCount; i++) {
            updatedSkuLocations.push({
              sku: String(currentMaxSku + i).padStart(2, '0'),
              location: defaultLocation
            });
          }
        }
      }

      console.log('Firestore 업데이트 시작...');
      // Firestore 업데이트
      await updateDoc(docRef, {
        ...editData,
        photos: updatedPhotos,
        skuLocations: updatedSkuLocations,
        updatedAt: serverTimestamp()
      });
      console.log('Firestore 업데이트 완료');

      // 이력 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: '수정',
        changes,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      // 위치 변경 시 알림 발송
      if (changes.location) {
        await sendLocationChangeNotification(
          id,
          editData.productName,
          changes.location.old,
          changes.location.new,
          user.email
        );
      }

      console.log('수정 완료!');
      setItem({ ...editData, photos: updatedPhotos, skuLocations: updatedSkuLocations });
      setEditing(false);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setDeletedImages([]);
      loadHistory();
      alert('수정되었습니다.');
    } catch (error) {
      console.error('❌ 수정 오류:', error);
      console.error('에러 상세:', error.message);
      alert('수정 중 오류가 발생했습니다: ' + error.message);
    }
  };

  const handleLocationChange = async (newLocation) => {
    try {
      const docRef = doc(db, 'inventory', id);

      await updateDoc(docRef, {
        location: newLocation,
        updatedAt: serverTimestamp()
      });

      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: '위치변경',
        previousValue: item.location,
        newValue: newLocation,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      // 앱 내 알림 발송
      await sendLocationChangeNotification(id, item.productName, item.location, newLocation, user.email);

      setItem(prev => ({ ...prev, location: newLocation }));
      setEditData(prev => ({ ...prev, location: newLocation }));
      loadHistory();
      alert('위치가 변경되었습니다.');
    } catch (error) {
      console.error('위치 변경 오류:', error);
      alert('위치 변경 중 오류가 발생했습니다.');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const docRef = doc(db, 'inventory', id);

      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: '상태변경',
        previousValue: item.status,
        newValue: newStatus,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      setItem(prev => ({ ...prev, status: newStatus }));
      setEditData(prev => ({ ...prev, status: newStatus }));
      loadHistory();
      alert('상태가 변경되었습니다.');
    } catch (error) {
      console.error('상태 변경 오류:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const handleCancelSale = async () => {
    if (!window.confirm('판매를 취소하시겠습니까? 상태가 "판매가능"으로 변경됩니다.')) {
      return;
    }

    try {
      const docRef = doc(db, 'inventory', id);

      await updateDoc(docRef, {
        status: '판매가능',
        updatedAt: serverTimestamp()
      });

      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: '판매취소',
        previousValue: '판매완료',
        newValue: '판매가능',
        timestamp: serverTimestamp(),
        userId: user.email
      });

      setItem(prev => ({ ...prev, status: '판매가능' }));
      setEditData(prev => ({ ...prev, status: '판매가능' }));
      loadHistory();
      alert('판매가 취소되었습니다.');
    } catch (error) {
      console.error('판매 취소 오류:', error);
      alert('판매 취소 중 오류가 발생했습니다.');
    }
  };

  // 재고 삭제 핸들러
  const handleDelete = async () => {
    try {
      // 이미지가 있으면 Storage에서도 삭제
      if (item.imageUrls && item.imageUrls.length > 0) {
        for (const url of item.imageUrls) {
          try {
            const imageRef = ref(storage, url);
            await deleteObject(imageRef);
          } catch (e) {
            console.log('이미지 삭제 실패 (이미 삭제되었거나 없음):', e);
          }
        }
      }

      // Firestore에서 문서 삭제
      await deleteDoc(doc(db, 'inventory', id));

      alert('재고가 삭제되었습니다.');
      navigate('/inventory');
    } catch (error) {
      console.error('재고 삭제 오류:', error);
      alert('재고 삭제 중 오류가 발생했습니다.');
    }
  };

  // 수량 변경 핸들러 (수량관리 타입 제품용)
  const handleQuantityChange = async (changeAmount) => {
    const currentQty = item.quantity || 1;
    const newQty = currentQty + changeAmount;

    if (newQty < 0) {
      alert('수량은 0 미만이 될 수 없습니다.');
      return;
    }

    const changeType = changeAmount > 0 ? '입고' : '출고';
    const confirmMessage = `${Math.abs(changeAmount)}개를 ${changeType} 처리하시겠습니까?\n현재: ${currentQty}개 → 변경 후: ${newQty}개`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const docRef = doc(db, 'inventory', id);

      // skuLocations 배열도 수량에 맞게 업데이트
      let updatedSkuLocations = item.skuLocations || [];

      if (changeAmount < 0 && updatedSkuLocations.length > 0) {
        // 출고: 뒤에서부터 SKU 제거
        const removeCount = Math.min(Math.abs(changeAmount), updatedSkuLocations.length);
        updatedSkuLocations = updatedSkuLocations.slice(0, updatedSkuLocations.length - removeCount);
      } else if (changeAmount > 0) {
        // 입고: 새 SKU 추가
        const currentMaxSku = updatedSkuLocations.length > 0
          ? Math.max(...updatedSkuLocations.map(s => parseInt(s.sku) || 0))
          : 0;
        const defaultLocation = item.location || '';

        for (let i = 1; i <= changeAmount; i++) {
          updatedSkuLocations.push({
            sku: String(currentMaxSku + i).padStart(2, '0'),
            location: defaultLocation
          });
        }
      }

      await updateDoc(docRef, {
        quantity: newQty,
        skuLocations: updatedSkuLocations,
        updatedAt: serverTimestamp()
      });

      // 이력 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: id,
        changeType: `수량${changeType}`,
        previousValue: `${currentQty}개`,
        newValue: `${newQty}개`,
        changeAmount: changeAmount,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      // Slack 알림
      await sendSlackNotification('quantityChange', {
        productName: item.productName,
        barcodeId: item.barcodeId,
        oldQuantity: currentQty,
        newQuantity: newQty
      }, user.email);

      // 앱 내 알림 발송
      await sendQuantityChangeNotification(id, item.productName, currentQty, newQty, user.email);

      setItem(prev => ({ ...prev, quantity: newQty, skuLocations: updatedSkuLocations }));
      setEditData(prev => ({ ...prev, quantity: newQty, skuLocations: updatedSkuLocations }));
      loadHistory();
      alert(`${changeType} 처리되었습니다. (${currentQty}개 → ${newQty}개)`);
    } catch (error) {
      console.error('수량 변경 오류:', error);
      alert('수량 변경 중 오류가 발생했습니다.');
    }
  };

  const printBarcode = () => {
    // 바코드 출력 페이지로 이동하며 현재 제품을 미리 선택
    navigate(`/barcode-print?preselect=${id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!item) {
    return <div className="text-center py-12 text-gray-500">재고를 찾을 수 없습니다.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">재고 상세</h2>
          <p className="text-sm text-gray-500 mt-1">
            상품 ID: <span className="font-mono text-brand-600">{item.barcodeId}</span>
          </p>
        </div>
        <div className="flex space-x-3">
          {item.status !== '판매완료' ? (
            <button
              onClick={() => navigate(`/sales?productId=${item.id}`)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition shadow-sm flex items-center font-medium"
            >
              <Icons.DollarSign className="w-4 h-4 mr-2" />
              판매 완료 처리
            </button>
          ) : (
            <button
              onClick={handleCancelSale}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition shadow-sm flex items-center font-medium"
            >
              <Icons.X className="w-4 h-4 mr-2" />
              판매 취소
            </button>
          )}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition shadow-sm flex items-center font-medium"
          >
            <Icons.Trash className="w-4 h-4 mr-2" />
            재고 삭제
          </button>
          <button
            onClick={() => navigate('/inventory')}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition shadow-sm font-medium"
          >
            목록으로
          </button>
        </div>
      </div>

      {/* 위치/Zone 불일치 경고 */}
      {locationMismatch && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <Icons.AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-amber-800 mb-1">위치/Zone 불일치</h4>
            <p className="text-sm text-amber-700">{locationMismatch.message}</p>
            <p className="text-xs text-amber-600 mt-2">
              실제 재고 위치와 Price List Zone이 다릅니다. "부분 이동"으로 위치를 변경하거나 Zone을 수정해주세요.
            </p>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-red-600 flex items-center">
                  <Icons.Trash className="w-5 h-5 mr-2" />
                  재고 삭제
                </h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                정말로 이 재고를 삭제하시겠습니까?
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">상품명:</span> {item?.productName}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">바코드:</span> {item?.barcodeId}
                </p>
                {item?.skuLocations && item.skuLocations.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">SKU 개수:</span> {item.skuLocations.length}개
                  </p>
                )}
              </div>
              {item?.skuLocations && item.skuLocations.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-700 font-medium flex items-center">
                    <Icons.AlertTriangle className="w-4 h-4 mr-1" />
                    {item.skuLocations.length}개의 SKU 정보가 함께 삭제됩니다
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    SKU: {item.skuLocations.slice(0, 5).map(s => s.sku).join(', ')}
                    {item.skuLocations.length > 5 && ` 외 ${item.skuLocations.length - 5}개`}
                  </p>
                </div>
              )}
              <p className="text-sm text-red-500 mb-6">
                삭제된 재고는 복구할 수 없습니다.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition font-medium"
                >
                  취소
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    handleDelete();
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium"
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SKU 부분 이동 모달 */}
      {showSkuMoveModal && item?.skuLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-brand-900 flex items-center">
                  <Icons.ArrowRight className="w-5 h-5 mr-2 text-brand-500" />
                  SKU 부분 이동
                </h3>
                <button
                  onClick={() => {
                    setShowSkuMoveModal(false);
                    setSelectedSkus([]);
                    setTargetLocation('');
                    setTargetZone({ id: '', name: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600 transition p-1"
                >
                  <Icons.X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">{item.productName}</p>
            </div>

            <div className="p-6 max-h-[50vh] overflow-y-auto">
              <div className="mb-4">
                <span className="text-sm font-medium text-gray-700">이동할 SKU 선택</span>
                <span className="text-xs text-brand-500 ml-2">
                  {selectedSkus.length}개 선택됨
                </span>
              </div>

              <div className="space-y-3">
                {Object.entries(groupSkusByLocation(item.skuLocations)).map(([location, skus]) => {
                  const skuIds = skus.map(s => s.sku);
                  const allSelected = skuIds.every(sku => selectedSkus.includes(sku));

                  return (
                    <div key={location} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleLocationSkus(location, skus)}
                            className="w-4 h-4 text-brand-600 rounded border-gray-300"
                          />
                          <Icons.MapPin className="w-3.5 h-3.5 text-brand-500" />
                          <span className="text-sm font-medium text-gray-700">{location}</span>
                          <span className="text-xs text-gray-400">({skus.length}개)</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 ml-6">
                        {skus.map(sku => (
                          <label
                            key={sku.sku}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition ${
                              selectedSkus.includes(sku.sku)
                                ? 'bg-brand-100 border border-brand-300'
                                : 'bg-white border border-gray-200 hover:border-brand-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedSkus.includes(sku.sku)}
                              onChange={() => toggleSkuSelection(sku.sku)}
                              className="w-3 h-3 text-brand-600 rounded border-gray-300"
                            />
                            <span className="text-xs font-mono text-gray-700">-{sku.sku}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  목적지 위치
                </label>
                <select
                  value={targetLocation}
                  onChange={(e) => setTargetLocation(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">위치 선택...</option>
                  {(allLocations.length > 0 ? allLocations : locations).map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zone 배치 <span className="text-gray-400 text-xs">(선택사항)</span>
                </label>
                <select
                  value={targetZone.id}
                  onChange={(e) => {
                    const zone = allZones.find(z => z.id === e.target.value);
                    setTargetZone(zone ? { id: zone.id, name: zone.name } : { id: '', name: '' });
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Zone 선택 (선택사항)...</option>
                  {allZones.map(zone => (
                    <option key={zone.id} value={zone.id}>{zone.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSkuMoveModal(false);
                    setSelectedSkus([]);
                    setTargetLocation('');
                    setTargetZone({ id: '', name: '' });
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
                >
                  취소
                </button>
                <button
                  onClick={handleSkuMove}
                  disabled={selectedSkus.length === 0 || !targetLocation}
                  className={`flex-1 px-4 py-2.5 rounded-lg transition font-medium flex items-center justify-center ${
                    selectedSkus.length === 0 || !targetLocation
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-brand-600 hover:bg-brand-700 text-white'
                  }`}
                >
                  <Icons.ArrowRight className="w-4 h-4 mr-2" />
                  {selectedSkus.length}개 이동하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SKU 부분 삭제 모달 */}
      {showSkuDeleteModal && item?.skuLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-red-700 flex items-center">
                  <Icons.Trash className="w-5 h-5 mr-2 text-red-500" />
                  SKU 부분 삭제
                </h3>
                <button
                  onClick={() => {
                    setShowSkuDeleteModal(false);
                    setSkusToDelete([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                삭제할 SKU를 선택하세요. 삭제된 SKU는 복구할 수 없습니다.
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              <div className="space-y-4">
                {Object.entries(groupSkusByLocation(item.skuLocations)).map(([location, skus]) => {
                  const skuIds = skus.map(s => s.sku);
                  const allSelected = skuIds.every(sku => skusToDelete.includes(sku));
                  const someSelected = skuIds.some(sku => skusToDelete.includes(sku)) && !allSelected;

                  return (
                    <div key={location} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icons.MapPin className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-700">{location}</span>
                          <span className="text-sm text-gray-400">({skus.length}개)</span>
                        </div>
                        <button
                          onClick={() => toggleLocationSkusDelete(location, skus)}
                          className={`text-xs px-2 py-1 rounded transition ${
                            allSelected
                              ? 'bg-red-100 text-red-700'
                              : someSelected
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {allSelected ? '전체 해제' : '전체 선택'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {skus.map(sku => {
                          const isSelected = skusToDelete.includes(sku.sku);
                          return (
                            <button
                              key={sku.sku}
                              onClick={() => toggleSkuDeleteSelection(sku.sku)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-mono transition ${
                                isSelected
                                  ? 'bg-red-500 text-white border-2 border-red-600'
                                  : 'bg-white border border-gray-300 hover:border-red-400 hover:bg-red-50'
                              }`}
                            >
                              {item.barcodeId}-{sku.sku}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50">
              {skusToDelete.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>{skusToDelete.length}개</strong>의 SKU가 삭제됩니다.
                    수량이 {item.quantity} → {item.quantity - skusToDelete.length}로 변경됩니다.
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSkuDeleteModal(false);
                    setSkusToDelete([]);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
                >
                  취소
                </button>
                <button
                  onClick={handleSkuDelete}
                  disabled={skusToDelete.length === 0}
                  className={`flex-1 px-4 py-2.5 rounded-lg transition font-medium flex items-center justify-center ${
                    skusToDelete.length === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  <Icons.Trash className="w-4 h-4 mr-2" />
                  {skusToDelete.length}개 삭제하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 확대 모달 */}
      {showImageModal && item?.photos && item.photos.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-7xl w-full h-full flex items-center justify-center">
            {/* 닫기 버튼 */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition z-10"
            >
              <Icons.X className="w-8 h-8" />
            </button>

            {/* 이전 버튼 */}
            {item.photos.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  previousImage();
                }}
                className="absolute left-4 text-white hover:text-gray-300 transition z-10 bg-black bg-opacity-50 rounded-full p-3"
              >
                <Icons.ChevronDown className="w-8 h-8 rotate-90" />
              </button>
            )}

            {/* 이미지 */}
            <div
              className="max-w-full max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={item.photos[selectedImageIndex]}
                alt={`${item.productName} ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
            </div>

            {/* 다음 버튼 */}
            {item.photos.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white hover:text-gray-300 transition z-10 bg-black bg-opacity-50 rounded-full p-3"
              >
                <Icons.ChevronDown className="w-8 h-8 -rotate-90" />
              </button>
            )}

            {/* 이미지 카운터 */}
            {item.photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full">
                {selectedImageIndex + 1} / {item.photos.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 판매유무/상태 옵션 관리 모달 */}
      {showStatusOptionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">드롭다운 옵션 관리</h3>
                <button
                  onClick={() => {
                    setShowStatusOptionsModal(false);
                    setStatusOptionsType('');
                    setNewOptionInput('');
                    setEditingOptionIndex(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <Icons.X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* 탭 선택 */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setStatusOptionsType('isSold')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                    statusOptionsType === 'isSold'
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  판매 유무
                </button>
                <button
                  onClick={() => setStatusOptionsType('status')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                    statusOptionsType === 'status'
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  상태
                </button>
              </div>

              {statusOptionsType && (
                <>
                  {/* 옵션 목록 */}
                  <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                    {(statusOptionsType === 'isSold' ? isSoldOptions : statusOptions).map((opt, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group">
                        {editingOptionIndex === index ? (
                          <>
                            <input
                              type="text"
                              value={editingOptionValue}
                              onChange={(e) => setEditingOptionValue(e.target.value)}
                              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleUpdateOption(index)}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                            >
                              <Icons.Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingOptionIndex(null);
                                setEditingOptionValue('');
                              }}
                              className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                            >
                              <Icons.X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className={`flex-1 text-sm ${getStatusColor(opt)} px-2 py-1 rounded`}>{opt}</span>
                            <button
                              onClick={() => {
                                setEditingOptionIndex(index);
                                setEditingOptionValue(opt);
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition"
                            >
                              <Icons.Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteOption(index)}
                              className="p-1.5 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                            >
                              <Icons.Trash className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* 새 옵션 추가 */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newOptionInput}
                      onChange={(e) => setNewOptionInput(e.target.value)}
                      placeholder="새 옵션 입력"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                    />
                    <button
                      onClick={handleAddOption}
                      className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition"
                    >
                      추가
                    </button>
                  </div>
                </>
              )}

              {!statusOptionsType && (
                <div className="text-center py-8 text-gray-500">
                  <Icons.Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>위 탭에서 수정할 항목을 선택하세요</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 컨디션 옵션 관리 모달 */}
      {showConditionOptionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">컨디션 옵션 관리</h3>
                <button
                  onClick={() => {
                    setShowConditionOptionsModal(false);
                    setNewConditionOptionInput('');
                    setEditingConditionIndex(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <Icons.X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* 옵션 목록 */}
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {conditionOptions.map((opt, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group">
                    {editingConditionIndex === index ? (
                      <>
                        <input
                          type="text"
                          value={editingConditionValue}
                          onChange={(e) => setEditingConditionValue(e.target.value)}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateConditionOption(index)}
                          className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                        >
                          <Icons.Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingConditionIndex(null);
                            setEditingConditionValue('');
                          }}
                          className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                        >
                          <Icons.X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`flex-1 text-sm px-2 py-1 rounded ${conditionColors[opt] || 'bg-gray-100 text-gray-700'}`}>{opt}</span>
                        <button
                          onClick={() => {
                            setEditingConditionIndex(index);
                            setEditingConditionValue(opt);
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition"
                        >
                          <Icons.Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteConditionOption(index)}
                          className="p-1.5 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                        >
                          <Icons.Trash className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* 새 옵션 추가 */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newConditionOptionInput}
                  onChange={(e) => setNewConditionOptionInput(e.target.value)}
                  placeholder="새 컨디션 옵션 입력"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddConditionOption()}
                />
                <button
                  onClick={handleAddConditionOption}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition"
                >
                  추가
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 왼쪽: 사진 및 바코드 */}
        <div className="lg:col-span-1 space-y-6 min-w-[280px] flex-shrink-0">
          {/* 사진 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-lg font-bold text-brand-900 mb-4 flex items-center">
              <Icons.Image className="w-5 h-5 mr-2 text-brand-500" />
              제품 사진
            </h3>

            {editing ? (
              <div className="space-y-4">
                {/* 기존 사진 */}
                {editData.photos && editData.photos.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 font-medium">기존 사진</p>
                    {editData.photos.map((photo, index) => (
                      <div key={index} className="relative rounded-lg overflow-hidden border border-gray-200 shadow-sm group">
                        <img
                          src={photo}
                          alt={`${item.productName} ${index + 1}`}
                          className="w-full h-auto object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => markImageForDeletion(photo)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <Icons.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 새 사진 미리보기 */}
                {newImagePreviews.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 font-medium">새로 추가할 사진</p>
                    {newImagePreviews.map((preview, index) => (
                      <div key={index} className="relative rounded-lg overflow-hidden border border-green-300 shadow-sm group">
                        <img
                          src={preview}
                          alt={`새 사진 ${index + 1}`}
                          className="w-full h-auto object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeNewImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <Icons.X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 사진 추가 버튼 */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-brand-500 transition-colors bg-gray-50">
                  <input
                    type="file"
                    id="edit-image-upload"
                    accept="image/*"
                    multiple
                    onChange={handleNewImageChange}
                    className="hidden"
                  />
                  <label htmlFor="edit-image-upload" className="cursor-pointer flex flex-col items-center justify-center">
                    <div className="w-12 h-12 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center mb-3">
                      <Icons.Plus className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">사진 추가</p>
                    <p className="text-xs text-gray-500 mt-1">클릭하여 이미지 선택</p>
                  </label>
                </div>
              </div>
            ) : (
              <>
                {item.photos && item.photos.length > 0 ? (
                  <div className="space-y-4">
                    {item.photos.map((photo, index) => (
                      <div
                        key={index}
                        className="rounded-lg overflow-hidden border border-gray-200 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => openImageModal(index)}
                      >
                        <img
                          src={photo}
                          alt={`${item.productName} ${index + 1}`}
                          className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-50 border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 rounded-lg">
                    <Icons.Image className="w-8 h-8 mb-2 opacity-50" />
                    <span>이미지 없음</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 바코드/QR */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-lg font-bold text-brand-900 mb-4 flex items-center">
              <Icons.QrCode className="w-5 h-5 mr-2 text-brand-500" />
              바코드 / QR 코드
            </h3>

            {qrCode && (
              <div className="text-center mb-6 bg-white p-4 rounded-lg border border-gray-100 inline-block w-full">
                <img src={qrCode} alt="QR Code" className="mx-auto w-32 h-32" />
              </div>
            )}

            <div className="text-center mb-4">
              <svg ref={barcodeRef} className="w-full h-auto"></svg>
            </div>

            <div className="flex items-center justify-center mb-6 bg-gray-50 py-2 px-3 rounded border border-gray-200">
              <span className="text-sm font-mono text-gray-600 flex-1 text-center">
                {item.barcodeId}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(item.barcodeId);
                }}
                className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                title="클립보드에 복사"
              >
                <Icons.Copy className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={printBarcode}
              className="w-full px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-sm font-medium flex items-center justify-center"
            >
              <Icons.Printer className="w-5 h-5 mr-2" />
              바코드 출력
            </button>
          </div>

          {/* 수량 관리 (수량관리 타입이거나 SKU가 2개 이상일 때 표시) */}
          {(item.productType === 'bulk' || (item.skuLocations && item.skuLocations.length > 1) || item.quantity > 1) && (
            <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
              <h3 className="text-lg font-bold text-brand-900 mb-4 flex items-center">
                <Icons.Box className="w-5 h-5 mr-2 text-brand-500" />
                재고 수량
              </h3>

              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-brand-700 mb-2">
                  {item.quantity || 1}
                </div>
                <span className="text-gray-500 text-lg">개</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  className="flex items-center justify-center px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition font-bold border border-red-200"
                >
                  <Icons.Minus className="w-5 h-5 mr-2" />
                  출고 (1개)
                </button>
                <button
                  onClick={() => handleQuantityChange(1)}
                  className="flex items-center justify-center px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition font-bold border border-green-200"
                >
                  <Icons.Plus className="w-5 h-5 mr-2" />
                  입고 (1개)
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => {
                    const amount = parseInt(prompt('출고할 수량을 입력하세요:') || '0');
                    if (amount > 0) handleQuantityChange(-amount);
                  }}
                  className="flex items-center justify-center px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition text-sm border border-gray-200"
                >
                  대량 출고
                </button>
                <button
                  onClick={() => {
                    const amount = parseInt(prompt('입고할 수량을 입력하세요:') || '0');
                    if (amount > 0) handleQuantityChange(amount);
                  }}
                  className="flex items-center justify-center px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition text-sm border border-gray-200"
                >
                  대량 입고
                </button>
              </div>

              {/* 수량 직접 수정 버튼 */}
              <button
                onClick={async () => {
                  const currentQty = item.quantity || 1;
                  const newQty = prompt(`현재 수량: ${currentQty}개\n\n새로운 수량을 입력하세요:`, currentQty);
                  if (newQty === null) return; // 취소

                  const parsedQty = parseInt(newQty);
                  if (isNaN(parsedQty) || parsedQty < 0) {
                    alert('올바른 수량을 입력해주세요.');
                    return;
                  }

                  if (parsedQty === currentQty) return; // 변경 없음

                  if (!window.confirm(`수량을 ${currentQty}개 → ${parsedQty}개로 변경하시겠습니까?`)) return;

                  try {
                    const itemRef = doc(db, 'inventory', id);
                    await updateDoc(itemRef, { quantity: parsedQty });

                    // 이력 기록
                    await addDoc(collection(db, 'inventory_history'), {
                      productId: id,
                      changeType: '수량수정',
                      previousValue: String(currentQty),
                      newValue: String(parsedQty),
                      timestamp: serverTimestamp(),
                      userId: user?.email || 'unknown'
                    });

                    alert(`수량이 ${parsedQty}개로 변경되었습니다.`);
                    loadItem();
                    loadHistory();
                  } catch (error) {
                    console.error('수량 수정 오류:', error);
                    alert('수량 수정 중 오류가 발생했습니다.');
                  }
                }}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition font-medium border border-blue-200"
              >
                <Icons.Edit className="w-4 h-4 mr-2" />
                수량 직접 수정
              </button>

              {(item.quantity || 1) === 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 font-medium text-center">
                    ⚠️ 재고가 소진되었습니다
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 오른쪽: 상세 정보 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 기본 정보 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-8">
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
              <h3 className="text-xl font-bold text-brand-900 flex items-center">
                <Icons.Info className="w-6 h-6 mr-2 text-brand-500" />
                기본 정보
              </h3>
              {!editing ? (
                <button
                  onClick={handleEdit}
                  className="px-4 py-2 bg-brand-50 text-brand-700 hover:bg-brand-100 rounded-lg transition font-medium flex items-center"
                >
                  <Icons.Edit className="w-4 h-4 mr-2" />
                  수정
                </button>
              ) : (
                <div className="space-x-2 flex">
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium flex items-center"
                  >
                    <Icons.Check className="w-4 h-4 mr-2" />
                    저장
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition font-medium"
                  >
                    취소
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <InfoRow label="제품명" value={editing ? editData.productName : item.productName} editing={editing} onChange={(val) => setEditData(prev => ({ ...prev, productName: val }))} />
              <InfoRow
                label="SKU"
                value={editing ? editData.barcodeId : item.barcodeId}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, barcodeId: val }))}
              />
              {/* 다량 제품 SKU 코드 목록 - 위치별 그룹화 */}
              {(Number(item.quantity) > 1 || item.productType === 'bulk') && !editing && (
                <div className="py-3 border-b border-gray-50 px-2">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-500 font-medium">SKU 코드 목록</span>
                    {item.skuLocations && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowSkuMoveModal(true)}
                          className="text-xs px-3 py-1.5 bg-brand-50 text-brand-600 hover:bg-brand-100 rounded-lg transition font-medium flex items-center"
                        >
                          <Icons.ArrowRight className="w-3 h-3 mr-1" />
                          부분 이동
                        </button>
                        <button
                          onClick={() => setShowSkuDeleteModal(true)}
                          className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition font-medium flex items-center"
                        >
                          <Icons.Trash className="w-3 h-3 mr-1" />
                          부분 삭제
                        </button>
                      </div>
                    )}
                  </div>

                  {/* skuLocations가 있으면 위치별 그룹화 + 테이블 표시 */}
                  {item.skuLocations ? (
                    <div className="space-y-4">
                      {Object.entries(groupSkusByLocation(item.skuLocations)).map(([location, skus]) => (
                        <div key={location} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-3">
                            <Icons.MapPin className="w-3.5 h-3.5 text-brand-500" />
                            <span className="text-xs font-semibold text-gray-700">{location}</span>
                            <span className="text-xs text-gray-400">({skus.length}개)</span>
                          </div>
                          {/* SKU 테이블 */}
                          <table className="w-full table-fixed">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left text-xs font-semibold text-gray-600 pb-2" style={{width: '28%'}}>Code</th>
                                <th className="text-center text-xs font-semibold text-gray-600 pb-2 px-3" style={{width: '10%'}}>상태</th>
                                <th className="text-center text-xs font-semibold text-gray-600 pb-2 px-3" style={{width: '14%'}}>판매</th>
                                <th className="text-left text-xs font-semibold text-gray-600 pb-2 pl-4">비고</th>
                              </tr>
                            </thead>
                            <tbody>
                              {skus.map(sku => (
                                <tr key={sku.sku} className="border-b border-gray-100 last:border-b-0">
                                  {/* Code 열 */}
                                  <td className="py-2">
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs font-mono text-gray-700">
                                        {item.barcodeId}-{sku.sku}
                                      </span>
                                      {sku.zoneName && (
                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                                          {sku.zoneName}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {/* 상태 열 */}
                                  <td className="py-2 text-center px-3">
                                    <div className="relative inline-block">
                                      <button
                                        onClick={() => setSelectedSkuForCondition(selectedSkuForCondition === sku.sku ? null : sku.sku)}
                                        className={`px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${conditionColors[sku.condition] || conditionColors['중']} hover:opacity-80 transition`}
                                      >
                                        {sku.condition || item.condition || '중'}
                                      </button>
                                      {selectedSkuForCondition === sku.sku && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden min-w-[60px]">
                                          {conditionOptions.map(opt => (
                                            <button
                                              key={opt}
                                              onClick={() => handleSkuConditionChange(sku.sku, opt)}
                                              className={`block w-full text-left px-3 py-1.5 text-xs whitespace-nowrap hover:bg-gray-50 ${sku.condition === opt ? 'font-bold bg-gray-50' : ''}`}
                                            >
                                              {opt}
                                            </button>
                                          ))}
                                          <div className="border-t border-gray-100 mt-1 pt-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedSkuForCondition(null);
                                                setShowConditionOptionsModal(true);
                                              }}
                                              className="flex items-center justify-center w-full py-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                                              title="옵션 관리"
                                            >
                                              <Icons.Settings className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  {/* 판매 열 */}
                                  <td className="py-2 text-center px-3">
                                    <div className="relative inline-block">
                                      <button
                                        onClick={() => setSelectedSkuForStatus(selectedSkuForStatus === sku.sku ? null : sku.sku)}
                                        className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap transition ${
                                          (sku.status || item.status) === '판매완료'
                                            ? 'bg-gray-100 text-gray-500'
                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                        }`}
                                      >
                                        {sku.status || item.status || '판매가능'}
                                      </button>
                                      {selectedSkuForStatus === sku.sku && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden min-w-[80px]">
                                          {statusOptions.map(opt => (
                                            <button
                                              key={opt}
                                              onClick={() => handleSkuStatusChange(sku.sku, opt)}
                                              className={`block w-full text-left px-3 py-1.5 text-xs whitespace-nowrap hover:bg-gray-50 ${(sku.status || item.status) === opt ? 'font-bold bg-gray-50' : ''}`}
                                            >
                                              {opt}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  {/* 비고 열 */}
                                  <td className="py-2 pl-4">
                                    {editingConditionNote === sku.sku ? (
                                      <input
                                        type="text"
                                        value={conditionNoteInput}
                                        onChange={(e) => setConditionNoteInput(e.target.value)}
                                        onBlur={() => handleSaveConditionNote(sku.sku)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSaveConditionNote(sku.sku)}
                                        placeholder="비고 입력"
                                        className="text-xs text-gray-700 bg-white border border-gray-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        autoFocus
                                      />
                                    ) : (
                                      <div
                                        onClick={() => startEditConditionNote(sku)}
                                        className={`text-xs cursor-pointer truncate ${sku.conditionNote ? 'text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
                                        title={sku.conditionNote || '클릭하여 메모 추가'}
                                      >
                                        {sku.conditionNote || '-'}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* skuLocations 초기화 전에는 기존 방식으로 표시 */
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: Number(item.quantity) || 1 }, (_, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-700">
                          {item.barcodeId}-{String(i + 1).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <InfoRow
                label="카테고리(대)"
                value={editing ? (editData.categoryLarge || '-') : (item.categoryLarge || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, categoryLarge: val }))}
              />
              <InfoRow
                label="카테고리(중)"
                value={editing ? (editData.categoryMedium || '-') : (item.categoryMedium || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, categoryMedium: val }))}
              />
              <InfoRow
                label="디자이너"
                value={editing ? (editData.designer || '-') : (item.designer || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, designer: val }))}
              />
              <InfoRow
                label="제조사"
                value={editing ? (editData.manufacturer || '-') : (item.manufacturer || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, manufacturer: val }))}
              />
              <InfoRow
                label="수종/색상"
                value={editing ? (editData.material || '-') : (item.material || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, material: val }))}
              />
              <InfoRow
                label="연대 (Era)"
                value={editing ? (editData.era || '-') : (item.era || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, era: val }))}
              />
              <InfoRow
                label="사이즈"
                value={editing ? (editData.size || '-') : (item.size || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, size: val }))}
              />
              {/* 재고 1개일 때만 제품 컨디션 표시, 여러개면 SKU별 컨디션으로 관리 */}
              {(Number(item.quantity) <= 1) && (
                <InfoRow
                  label="컨디션"
                  value={editing ? editData.condition : item.condition}
                  editing={editing}
                  editable
                  options={['최상', '상', '중', '하']}
                  onChange={(val) => setEditData(prev => ({ ...prev, condition: val }))}
                />
              )}
              {hasBulkSkuLocations ? (
                // 다량 제품: SKU 위치에서 자동 파생 (읽기 전용)
                <InfoRow
                  label="위치"
                  value={getSkuLocationSummary(item.skuLocations) || '-'}
                  highlight
                  helperText="SKU 위치에서 자동 표시 (부분 이동으로 변경)"
                />
              ) : (
                // 단품: 기존 드롭다운
                <InfoRow
                  label="위치"
                  value={editing ? editData.location : item.location}
                  editable
                  options={locations}
                  onChange={handleLocationChange}
                  highlight
                />
              )}
              <InfoRow
                label="Zone"
                value={editing
                  ? (allZones.find(z => z.id === editData.zoneId)
                    ? `${allZones.find(z => z.id === editData.zoneId).locationName} - ${allZones.find(z => z.id === editData.zoneId).name}`
                    : '-')
                  : (item.zoneName || '-')}
                editing={editing}
                editable
                options={['-', ...allZones.map(z => `${z.locationName} - ${z.name}`)]}
                onChange={(val) => {
                  if (val === '-') {
                    setEditData(prev => ({ ...prev, zoneId: '', zoneName: '' }));
                  } else {
                    const selectedZone = allZones.find(z => `${z.locationName} - ${z.name}` === val);
                    if (selectedZone) {
                      setEditData(prev => ({
                        ...prev,
                        zoneId: selectedZone.id,
                        zoneName: `${selectedZone.locationName} - ${selectedZone.name}`
                      }));
                    }
                  }
                }}
              />
              <InfoRow
                label="입고일(날짜)"
                value={editing ? (editData.arrivalDate || '-') : (item.arrivalDate || '-')}
                editing={editing}
                type="date"
                onChange={(val) => setEditData(prev => ({ ...prev, arrivalDate: val }))}
              />
              <InfoRow
                label="구매일"
                value={editing ? (editData.purchaseDate || '-') : (item.purchaseDate || '-')}
                editing={editing}
                type="date"
                onChange={(val) => setEditData(prev => ({ ...prev, purchaseDate: val }))}
              />
              <InfoRow
                label="입고일(컨테이너)"
                value={editing ? (editData.arrivalBatch || '') : (item.arrivalBatch || '-')}
                editing={editing}
                onChange={(val) => setEditData(prev => ({ ...prev, arrivalBatch: val }))}
              />
              <div className="relative group">
                <button
                  onClick={() => {
                    setStatusOptionsType('');
                    setShowStatusOptionsModal(true);
                  }}
                  className="absolute -top-1 right-0 p-1.5 rounded-lg bg-white border border-gray-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-gray-50"
                  title="옵션 설정"
                >
                  <Icons.Settings className="w-4 h-4 text-gray-500" />
                </button>
                <div className="grid grid-cols-2 gap-4">
                  <InfoRow
                    label="판매 유무"
                    value={item.isSold || (item.status === '판매완료' ? '판매완료' : '판매중')}
                    editable
                    options={isSoldOptions}
                    onChange={(val) => setEditData(prev => ({ ...prev, isSold: val }))}
                    statusBadge
                  />
                  <InfoRow
                    label="상태"
                    value={editing ? editData.status : item.status}
                    editable
                    options={statusOptions}
                    onChange={handleStatusChange}
                    statusBadge
                  />
                </div>
              </div>
              <InfoRow
                label="소유 구분"
                value={editing ? (editData.ownershipType || '직판') : (item.ownershipType || '직판')}
                editing={editing}
                editable
                options={['직판', '위탁']}
                onChange={(val) => setEditData(prev => ({ ...prev, ownershipType: val }))}
              />
              {((editing ? editData.ownershipType : item.ownershipType) === '위탁' || item.category === '위탁') && (
                <div className="mt-4 mb-4 bg-brand-50/50 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-brand-800 mb-3 uppercase tracking-wider flex items-center">
                    <Icons.Users className="w-4 h-4 mr-2" />
                    위탁자 정보
                  </h4>
                  <div className="space-y-1">
                    <InfoRow
                      label="위탁자"
                      value={editing ? (editData.consignorName || '') : (item.consignorName || '-')}
                      editing={editing}
                      onChange={(val) => setEditData(prev => ({ ...prev, consignorName: val }))}
                    />
                    <InfoRow
                      label="연락처"
                      value={editing ? (editData.consignorContact || '') : (item.consignorContact || '-')}
                      editing={editing}
                      onChange={(val) => setEditData(prev => ({ ...prev, consignorContact: val }))}
                    />
                    <InfoRow
                      label="수수료율"
                      value={editing ? (editData.consignorCommission || '') : `${item.consignorCommission || 0}%`}
                      editing={editing}
                      type="number"
                      onChange={(val) => setEditData(prev => ({ ...prev, consignorCommission: val }))}
                    />
                    <InfoRow
                      label="정산가"
                      value={editing ? (editData.settlementPrice || item.settlementPrice || '') : (item.settlementPrice ? `${Number(item.settlementPrice).toLocaleString()}원` : '-')}
                      editing={editing}
                      isPrice={true}
                      onChange={(val) => setEditData(prev => ({ ...prev, settlementPrice: val }))}
                    />
                  </div>
                </div>
              )}
              <InfoRow
                label="제품 유형"
                value={editing
                  ? ((editData.productType === 'bulk' || editData.quantity > 1) ? '다량' : '일품')
                  : ((item.productType === 'bulk' || item.quantity > 1) ? '다량' : '일품')}
                editing={editing}
                editable
                options={['일품', '다량']}
                onChange={(val) => setEditData(prev => ({
                  ...prev,
                  productType: val === '다량' ? 'bulk' : 'single',
                  quantity: val === '일품' ? 1 : (prev.quantity > 1 ? prev.quantity : 2)
                }))}
              />
              {(editing ? (editData.productType === 'bulk' || editData.quantity > 1) : (item.productType === 'bulk' || item.quantity > 1)) && (
                <InfoRow
                  label="수량"
                  value={editing ? (editData.quantity || 1) : `${item.quantity || 1}개`}
                  editing={editing}
                  type="number"
                  onChange={(val) => setEditData(prev => ({ ...prev, quantity: parseInt(val) || 1 }))}
                />
              )}
              <InfoRow
                label="복원완료"
                value={editing ? (editData.restorationCompleted ? '예' : '아니오') : (item.restorationCompleted ? '예' : '아니오')}
                editing={editing}
                editable
                options={['예', '아니오']}
                onChange={(val) => setEditData(prev => ({ ...prev, restorationCompleted: val === '예' }))}
              />
              <InfoRow
                label="판매가(부가세 미포함)"
                value={editing ? (editData.salePrice || 0) : `${parseInt(item.salePrice || 0).toLocaleString()}원`}
                editing={editing}
                type="number"
                onChange={(val) => setEditData(prev => ({ ...prev, salePrice: val }))}
                isPrice
              />
              {!editing && (item.salePrice > 0) && (
                <div className="flex justify-between py-3 border-b border-gray-50 px-2 bg-brand-50/30">
                  <span className="text-sm text-gray-500 font-medium">판매가(부가세 포함)</span>
                  <span className="text-sm font-bold text-brand-600">
                    {Math.round(parseInt(item.salePrice || 0) * 1.1).toLocaleString()}원
                  </span>
                </div>
              )}
              {item.purchasePrice > 0 && (
                <InfoRow
                  label="구매가"
                  value={editing ? (editData.purchasePrice || 0) : `${parseInt(item.purchasePrice).toLocaleString()}원`}
                  editing={editing}
                  type="number"
                  onChange={(val) => setEditData(prev => ({ ...prev, purchasePrice: val }))}
                  isPrice
                />
              )}
              {item.notes && (
                <InfoRow
                  label="특이사항"
                  value={item.notes}
                  editing={editing}
                  onChange={(val) => setEditData(prev => ({ ...prev, notes: val }))}
                />
              )}

              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-500 mb-2 uppercase tracking-wider">비고</h4>
                {editing ? (
                  <textarea
                    value={editData.notes || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    rows="3"
                    placeholder="비고를 입력하세요"
                  />
                ) : (
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {item.notes || '비고 없음'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 이력 */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-8">
            <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
              <Icons.History className="w-6 h-6 mr-2 text-brand-500" />
              변경 이력
            </h3>
            <div className="space-y-0 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
              {history.length > 0 ? (
                history.map((record, index) => (
                  <div key={record.id} className="relative pl-6 pb-6 border-l-2 border-gray-100 last:pb-0 last:border-l-0">
                    <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-brand-300 ring-4 ring-white"></div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                      <div>
                        <p className="font-bold text-gray-800 text-sm">{record.changeType}</p>
                        {record.previousValue && record.newValue && (
                          <p className="text-sm text-gray-600 mt-1 bg-gray-50 inline-block px-2 py-1 rounded">
                            <span className="line-through text-gray-400">{record.previousValue}</span>
                            <span className="mx-2 text-gray-400">→</span>
                            <span className="text-brand-600 font-medium">{record.newValue}</span>
                          </p>
                        )}
                        {record.changes && (
                          <div className="mt-1 text-sm text-gray-600">
                            {Object.keys(record.changes).map(key => (
                              <div key={key} className="mb-1">
                                <span className="font-medium text-gray-500">{key}: </span>
                                <span className="line-through text-gray-400">{record.changes[key].old}</span>
                                <span className="mx-1">→</span>
                                <span className="text-brand-600">{record.changes[key].new}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1 sm:mt-0 text-right">
                        <p>{record.timestamp?.toDate?.().toLocaleDateString()}</p>
                        <p className="mt-1 text-gray-500">{record.timestamp?.toDate?.().toLocaleTimeString()}</p>
                        <p className="mt-1 text-brand-600 font-medium">{getUserDisplayName(record.userId)}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Icons.History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>이력이 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, editing, editable, options, onChange, type = 'text', highlight, statusBadge, isPrice, helperText }) {
  const [showOptions, setShowOptions] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // editing이 명시적으로 false로 전달된 경우 editable 무시
  const showEditableDropdown = editable && editing !== false;

  // 가격 입력 핸들러 - 숫자만 입력받고 천단위 콤마 적용
  const handlePriceChange = (e) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    onChange(numericValue);
  };

  // 가격 표시 값 (천단위 콤마)
  const displayValue = isPrice
    ? parseInt(String(value).replace(/[^0-9]/g, '') || 0).toLocaleString()
    : value;

  return (
    <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors px-2 rounded-lg ${highlight ? 'bg-brand-50/30' : ''}`}>
      <span className="text-gray-500 font-medium text-sm mb-1 sm:mb-0 w-24">{label}</span>
      <div className="relative flex-1 sm:text-right">
        {editing ? (
          editable && options ? (
            <select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm bg-white"
            >
              {options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <input
              type={isPrice ? 'text' : type}
              value={displayValue}
              onChange={isPrice ? handlePriceChange : (e) => onChange(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm text-right"
            />
          )
        ) : (
          showEditableDropdown ? (
            <div className="relative inline-block" ref={dropdownRef}>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className={`px-3 py-1.5 rounded-lg text-sm transition flex items-center ml-auto ${statusBadge
                    ? getStatusColor(value)
                    : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm'
                  }`}
              >
                {value}
                <Icons.ChevronDown className="w-3 h-3 ml-2 opacity-50" />
              </button>
              {showOptions && (
                <div className="absolute left-0 sm:left-auto sm:right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-10 w-max max-w-xs overflow-hidden animate-fade-in-up">
                  {options.map(opt => (
                    <button
                      key={opt}
                      onClick={() => {
                        onChange(opt);
                        setShowOptions(false);
                      }}
                      className={`block w-full text-left px-4 py-2.5 hover:bg-brand-50 text-sm transition-colors whitespace-nowrap ${value === opt ? 'text-brand-600 font-bold bg-brand-50/50' : 'text-gray-700'
                        }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <span className={`font-medium ${isPrice ? 'text-brand-700 font-bold' : 'text-gray-900'}`}>
                {value}
              </span>
              {helperText && (
                <span className="text-xs text-gray-400 mt-0.5">{helperText}</span>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case '판매가능': return 'bg-green-100 text-green-800 border border-green-200';
    case '판매중': return 'bg-green-100 text-green-800 border border-green-200';
    case '판매완료': return 'bg-gray-100 text-gray-800 border border-gray-200';
    case '예약': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case '수리중': return 'bg-red-100 text-red-800 border border-red-200';
    case '청소중': return 'bg-blue-100 text-blue-800 border border-blue-200';
    default: return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
}

export default InventoryDetail;
